import React, { useCallback, useEffect, useRef, useState } from "react";
import {
  Alert,
  AlertTitle,
  AppBar,
  Box,
  Button,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  IconButton,
  Pagination,
  Paper,
  Snackbar,
  Stack,
  Step,
  StepLabel,
  Stepper,
  TextField,
  Toolbar,
  Typography,
} from "@mui/material";
import CalendarMonthIcon from "@mui/icons-material/CalendarMonth";
import CloseIcon from "@mui/icons-material/Close";
import api from "../axiosConfig";
import AudienceBuilder from "../components/massEmail/AudienceBuilder";
import AudienceReview from "../components/massEmail/AudienceReview";
import CampaignConfirmation from "../components/massEmail/CampaignConfirmation";
import Composer from "../components/massEmail/Composer";
import MassEmailStatus from "../components/MassEmailStatus";

// Harare = UTC+2, no DST, fixed forever. All helpers use explicit arithmetic — zero browser-timezone dependency.
const HARARE_OFFSET_MS = 2 * 60 * 60 * 1000;
const HARARE_OFFSET_STR = "+02:00";
const MONTHS = ["Jan","Feb","Mar","Apr","May","Jun","Jul","Aug","Sep","Oct","Nov","Dec"];

// Normalize bare UTC API strings (e.g. "2026-05-26 02:05:00" — no Z suffix) → add "Z".
function toUtcIso(raw) {
  if (!raw) return null;
  const s = String(raw).trim();
  if (/[zZ]$/.test(s) || /[+-]\d{2}:\d{2}$/.test(s)) return s;
  return (s.includes(" ") ? s.replace(" ", "T") : s) + "Z";
}

// UTC Date → "YYYY-MM-DDTHH:mm" in Harare time (value for <input type="datetime-local">).
function toHarareDatetimeValue(utcDate) {
  const h = new Date(utcDate.getTime() + HARARE_OFFSET_MS);
  return `${h.getUTCFullYear()}-${String(h.getUTCMonth()+1).padStart(2,"0")}-${String(h.getUTCDate()).padStart(2,"0")}T${String(h.getUTCHours()).padStart(2,"0")}:${String(h.getUTCMinutes()).padStart(2,"0")}`;
}

// "YYYY-MM-DDTHH:mm" treated as Harare time → UTC Date (browser timezone irrelevant).
function parseHarareDatetimeInput(localStr) {
  if (!localStr) return null;
  const d = new Date(localStr.slice(0, 16) + ":00" + HARARE_OFFSET_STR);
  return Number.isNaN(d.getTime()) ? null : d;
}

// "YYYY-MM-DDTHH:mm" Harare string → "YYYY-MM-DDTHH:mm:00+02:00" for API.
function toHarareOffsetIso(localStr) {
  if (!localStr) return null;
  return localStr.slice(0, 16) + ":00" + HARARE_OFFSET_STR;
}

// Format a UTC API datetime (bare or with Z) → "DD Mon YYYY, HH:mm" in Harare.
function fmtHarare(raw) {
  if (!raw) return "—";
  try {
    const dt = new Date(toUtcIso(raw));
    if (Number.isNaN(dt.getTime())) return "—";
    const h = new Date(dt.getTime() + HARARE_OFFSET_MS);
    return `${String(h.getUTCDate()).padStart(2,"0")} ${MONTHS[h.getUTCMonth()]} ${h.getUTCFullYear()}, ${String(h.getUTCHours()).padStart(2,"0")}:${String(h.getUTCMinutes()).padStart(2,"0")}`;
  } catch { return "—"; }
}

export default function CreateEmailCampaignModal({ open, onClose, onSuccess, intelligenceAudienceId = null }) {
  const [activeStep, setActiveStep] = useState(0);
  const steps = ["Audience", "Review", "Message", "Confirm & Schedule"];

  const [audienceChoice, setAudienceChoice] = useState("all_eligible");
  const [source, setSource] = useState("email_contacts");
  const [filters, setFilters] = useState({ q: "", source: "", segment: "", limit: 200, preferred_limit: "" });
  const [excludeRecent, setExcludeRecent] = useState(true);
  const [recentDays, setRecentDays] = useState(7);
  // Delivery pacing — owned here so AudienceBuilder controls it and Composer submits it.
  const [pacingMode, setPacingMode] = useState("immediate"); // "immediate" | "spread"
  const [pacingDays, setPacingDays] = useState(0);           // 0 | 7 | 30

  const [previewRows, setPreviewRows] = useState([]);
  const [audienceSummary, setAudienceSummary] = useState(null);
  const [audiencePlan, setAudiencePlan] = useState(null);

  const [audienceById, setAudienceById] = useState(() => new Map());
  const [selectedIds, setSelectedIds] = useState([]);

  const [subject, setSubject] = useState("");
  const [bodyHtml, setBodyHtml] = useState("");
  const [afterSendStatus, setAfterSendStatus] = useState("");
  // Reply-to email selector: 'default' | 'my_email' | 'other'
  const [replyToMode, setReplyToMode]               = useState("default");
  const [replyToCustomEmail, setReplyToCustomEmail] = useState("");
  const [personalisationMode, setPersonalisationMode] = useState("none");

  const [signature, setSignature] = useState({
    enabled: true,
    senderName: "",
    title: "",
    phone: "",
    email: "",
    website: "",
    address: "",
    imageUrl: null,
  });

  const [statusDialogOpen, setStatusDialogOpen] = useState(false);
  const [lastCampaignId, setLastCampaignId] = useState(null);

  const [loading, setLoading] = useState(false);
  const [budgetLoading, setBudgetLoading] = useState(false);
  const [emailBudget, setEmailBudget] = useState(null);
  const [currentUser, setCurrentUser] = useState(null);
  const [snack, setSnack] = useState({ open: false, msg: "", type: "info" });
  const [syncingEmailContacts, setSyncingEmailContacts] = useState(false);
  const [composerMeta, setComposerMeta] = useState({
    canSend: false,
    isSending: false,
  });

  const [scheduleOpen, setScheduleOpen] = useState(false);
  const [scheduleLocal, setScheduleLocal] = useState(() =>
    toHarareDatetimeValue(new Date(Date.now() + 60 * 60 * 1000))
  );
  const [audiencePreparedAt, setAudiencePreparedAt] = useState(null);
  const [sampleRemovedIds, setSampleRemovedIds] = useState(() => new Set());
  const [sampleAllRows, setSampleAllRows] = useState([]);
  const [reviewBarInfo, setReviewBarInfo] = useState(null);
  const [draftRestoreOpen, setDraftRestoreOpen] = useState(false);
  const [pendingDraft, setPendingDraft] = useState(null);
  const [cancelWithDraftOpen, setCancelWithDraftOpen] = useState(false);

  const launchRef = useRef(null);
  const draftSaveTimerRef = useRef(null);
  const canManageEmailBudget = Boolean(
    currentUser?.is_admin || ["marketing", "admin", "superadmin"].includes(String(currentUser?.role || "").toLowerCase().trim())
  );

  const loadEmailBudget = useCallback(async () => {
    setBudgetLoading(true);
    try {
      const response = await api.get("/analytics/marketing/broadcast-budget");
      setEmailBudget(response?.data?.data?.channels?.email || null);
    } catch {
      setEmailBudget(null);
    } finally {
      setBudgetLoading(false);
    }
  }, []);

  useEffect(() => {
    if (!open) {
      return;
    }

    setActiveStep(0);
    setAudienceChoice("all_eligible");
    setSource("email_contacts");
    setFilters({ q: "", source: "", segment: "", limit: 200, preferred_limit: "" });
    setExcludeRecent(true);
    setRecentDays(7);
    setPreviewRows([]);
    setAudienceSummary(null);
    setAudiencePlan(null);
    setAudienceById(new Map());
    setSelectedIds([]);
    setSubject("");
    setBodyHtml("");
    setAfterSendStatus("");
    setReplyToMode("default");
    setReplyToCustomEmail("");
    setStatusDialogOpen(false);
    setLastCampaignId(null);
    setLoading(false);
    setBudgetLoading(false);
    setEmailBudget(null);
    setSnack({ open: false, msg: "", type: "info" });
    setComposerMeta({ canSend: false, isSending: false });
    setAudiencePreparedAt(null);
    setSampleRemovedIds(new Set());
    setSampleAllRows([]);
    setReviewBarInfo(null);
    setCancelWithDraftOpen(false);
    launchRef.current = null;

    setScheduleLocal(toHarareDatetimeValue(new Date(Date.now() + 60 * 60 * 1000)));
    setScheduleOpen(false);
  }, [open]);

  useEffect(() => {
    if (!open) {
      return;
    }

    loadEmailBudget();
  }, [loadEmailBudget, open]);

  useEffect(() => {
    if (!open) return;
    try {
      const raw = localStorage.getItem("email_campaign_draft");
      if (!raw) return;
      const draft = JSON.parse(raw);
      const savedAt = draft?.savedAt ? new Date(draft.savedAt).getTime() : 0;
      if (Date.now() - savedAt > 24 * 60 * 60 * 1000) {
        localStorage.removeItem("email_campaign_draft");
        return;
      }
      if (!draft.subject && !draft.bodyHtml) return;
      setPendingDraft(draft);
      setDraftRestoreOpen(true);
    } catch {
      // ignore corrupt draft
    }
  }, [open]);

  useEffect(() => {
    if (!open) return;
    clearTimeout(draftSaveTimerRef.current);
    draftSaveTimerRef.current = setTimeout(() => {
      if (!subject && !bodyHtml) return;
      localStorage.setItem("email_campaign_draft", JSON.stringify({
        savedAt: new Date().toISOString(),
        audienceChoice,
        source,
        filters,
        excludeRecent,
        recentDays,
        subject,
        bodyHtml,
        replyToMode,
        replyToCustomEmail,
      }));
    }, 800);
    return () => clearTimeout(draftSaveTimerRef.current);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, audienceChoice, source, filters, excludeRecent, recentDays, subject, bodyHtml, replyToMode, replyToCustomEmail]);

  useEffect(() => {
    (async () => {
      try {
        const response = await api.get("/users/me");
        const data = response?.data || {};
        setCurrentUser(data);
        setSignature((current) => ({
          ...current,
          enabled: true,
          senderName: data.name || current.senderName,
          email: data.email || current.email,
          imageUrl: data.signature_url || null,
        }));
      } catch {
        // Ignore non-blocking signature errors.
      }
    })();
  }, []);

  const syncEmailContacts = async () => {
    setSyncingEmailContacts(true);
    try {
      const response = await api.post("/email-contacts/sync-now", { source: "all" });
      const localSynced = Number(response?.data?.local_sync?.synced || 0);
      setSnack({
        open: true,
        msg: localSynced > 0
          ? `Email contacts refreshed. ${localSynced.toLocaleString()} unified contacts are now available for campaigns.`
          : "Email contacts sync started.",
        type: "success",
      });
    } catch (error) {
      setSnack({
        open: true,
        msg: error?.response?.data?.message || error?.message || "Failed to start email contacts sync.",
        type: "error",
      });
    } finally {
      setSyncingEmailContacts(false);
    }
  };

  const handleAudiencePrepared = (payload) => {
    if (!payload) {
      setPreviewRows([]);
      setAudienceSummary(null);
      setAudiencePlan(null);
      setAudienceById(new Map());
      setSelectedIds([]);
      setAudiencePreparedAt(null);
      return;
    }

    setPreviewRows(payload.previewRows || []);
    setAudienceSummary(payload.summary || null);
    setAudiencePlan(payload.plan || null);
    setAudiencePreparedAt(Date.now());

    if (payload.plan?.deliveryMode !== "raw_list") {
      setAudienceById(new Map());
      setSelectedIds([]);
    }
  };

  const handleImportRows = (rows) => {
    const nextRows = Array.isArray(rows) ? rows : [];
    setAudienceById(() => {
      const next = new Map();
      nextRows.forEach((row) => next.set(String(row.id), row));
      return next;
    });
    setSelectedIds(nextRows.map((row) => String(row.id)));
  };

  const handleSampleExclude = useCallback(({ removedIds, allRows } = {}) => {
    setSampleRemovedIds(removedIds instanceof Set ? removedIds : new Set());
    setSampleAllRows(Array.isArray(allRows) ? allRows : []);
  }, []);

  const handleNextFromReview = () => {
    if (sampleRemovedIds.size > 0) {
      if (audiencePlan?.deliveryMode === "filter_all") {
        // Keep filter_all and pass excluded IDs server-side. Composer spreads
        // audiencePlan.filters into the payload so excluded_contact_ids flows
        // through automatically.
        setAudiencePlan((prev) =>
          prev
            ? {
                ...prev,
                filters: {
                  ...prev.filters,
                  excluded_contact_ids: Array.from(sampleRemovedIds),
                },
              }
            : prev
        );
      } else {
        // page / import: explicit list manipulation is correct.
        const sourceRows = sampleAllRows.length > 0 ? sampleAllRows : previewRows;
        const keptRows = sourceRows.filter((r) => !sampleRemovedIds.has(r.id));
        const newMap = new Map(keptRows.map((r) => [String(r.id), r]));
        setAudienceById(newMap);
        setSelectedIds(keptRows.map((r) => String(r.id)));
        setAudiencePlan((prev) => prev ? { ...prev, deliveryMode: "raw_list", manualSelection: false } : prev);
      }
    }
    setActiveStep(2);
  };

  const handleUseAudience = () => {
    if (!audienceSummary || !audiencePlan) {
      return;
    }

    if (audiencePlan.deliveryMode === "filter_all" && Number(audienceSummary?.eligibleFound || 0) <= 0) {
      return;
    }

    if ((audiencePlan.deliveryMode === "page" || audiencePlan.deliveryMode === "raw_list")
      && Number(audienceSummary?.totalFound || audienceSummary?.eligibleFound || 0) <= 0) {
      return;
    }

    setActiveStep(1);
  };

  const onQueued = (campaignId) => {
    if (!campaignId) {
      return;
    }

    setLastCampaignId(campaignId);
    localStorage.removeItem("email_campaign_draft");
    // Delay close so the user sees the success confirmation before the modal disappears
    setTimeout(() => onSuccess?.(), 2500);
  };

  if (statusDialogOpen && lastCampaignId) {
    return (
      <Dialog open={open} fullScreen onClose={onClose}>
        <MassEmailStatus campaignId={lastCampaignId} onBack={() => setStatusDialogOpen(false)} />
      </Dialog>
    );
  }

  const canMoveForward = (() => {
    if (activeStep === 1) {
      if (!audiencePlan) {
        return false;
      }

      if (audiencePlan.deliveryMode === "page" || audiencePlan.deliveryMode === "raw_list") {
        return selectedIds.length > 0;
      }

      return Number(audienceSummary?.eligibleFound || 0) > 0;
    }

    if (activeStep === 2) {
      return composerMeta.canSend;
    }

    return false;
  })();

  const openScheduleDialog = () => {
    const current = parseHarareDatetimeInput(scheduleLocal);
    if (!current || current <= new Date()) {
      setScheduleLocal(toHarareDatetimeValue(new Date(Date.now() + 60 * 60 * 1000)));
    }
    setScheduleOpen(true);
  };

  const handleConfirmSchedule = async (scheduledAt = scheduleLocal) => {
    if (scheduledAt !== null) {
      const dt = parseHarareDatetimeInput(scheduledAt);
      if (!dt) {
        setSnack({ open: true, msg: "Please choose a valid date and time.", type: "error" });
        return;
      }
      if (dt.getTime() <= Date.now() + 2 * 60 * 1000) {
        setSnack({ open: true, msg: "Schedule time must be at least 2 minutes in the future.", type: "error" });
        return;
      }
    }

    if (!audiencePreparedAt) {
      setSnack({ open: true, msg: "Please select an audience before scheduling.", type: "error" });
      return;
    }
    if (Date.now() - audiencePreparedAt > 20 * 60 * 1000) {
      setSnack({ open: true, msg: "Audience data is over 20 minutes old. Consider refreshing the audience selection.", type: "warning" });
    }

    setScheduleOpen(false);

    try {
      await launchRef.current?.({
        scheduledAt: scheduledAt ? toHarareOffsetIso(scheduledAt) : scheduledAt,
        tz: "Africa/Harare",
      });
    } catch (error) {
      setSnack({
        open: true,
        msg: error?.response?.data?.message || error?.message || "Failed to schedule campaign.",
        type: "error",
      });
    }
  };

  const handleCancel = () => {
    const hasContent = Boolean(subject || bodyHtml);
    if (hasContent && !lastCampaignId) {
      setCancelWithDraftOpen(true);
    } else {
      if (!hasContent) localStorage.removeItem("email_campaign_draft");
      onClose?.();
    }
  };

  const handleRestoreDraft = () => {
    if (!pendingDraft) return;
    setAudienceChoice(pendingDraft.audienceChoice || "all_eligible");
    setSource(pendingDraft.source || "email_contacts");
    setFilters(pendingDraft.filters || { q: "", source: "", segment: "", limit: 200, preferred_limit: "" });
    setExcludeRecent(pendingDraft.excludeRecent ?? true);
    setRecentDays(pendingDraft.recentDays ?? 7);
    setSubject(pendingDraft.subject || "");
    setBodyHtml(pendingDraft.bodyHtml || "");
    setReplyToMode(pendingDraft.replyToMode || "default");
    setReplyToCustomEmail(pendingDraft.replyToCustomEmail || "");
    if (pendingDraft.subject || pendingDraft.bodyHtml) {
      setActiveStep(2);
    }
    setDraftRestoreOpen(false);
    setPendingDraft(null);
  };

  return (
    <Dialog open={open} onClose={onClose} fullScreen>
      <AppBar
        position="sticky"
        elevation={0}
        sx={{
          bgcolor: "white",
          color: "#111827",
          borderBottom: "1px solid #E5E7EB",
        }}
      >
        <Toolbar sx={{ minHeight: 52, px: { xs: 1, md: 2 }, gap: 1 }}>
          <IconButton size="small" edge="start" onClick={onClose} disabled={loading || composerMeta.isSending}>
            <CloseIcon fontSize="small" />
          </IconButton>

          <Typography variant="subtitle1" sx={{ fontWeight: 900, flexShrink: 0, mr: 1 }}>
            Create Email Campaign
          </Typography>

          <Box sx={{ flex: 1, display: "flex", justifyContent: "center", overflow: "hidden", px: 1 }}>
            <Stepper
              activeStep={activeStep}
              sx={{
                "& .MuiStepLabel-label": { fontWeight: 800, fontSize: "0.72rem", whiteSpace: "nowrap" },
                "& .MuiStepIcon-root": { width: 20, height: 20 },
                "& .MuiStepConnector-line": { minWidth: 16 },
                "& .MuiStep-root": { px: 0.5 },
              }}
            >
              {steps.map((label) => (
                <Step key={label}>
                  <StepLabel>{label}</StepLabel>
                </Step>
              ))}
            </Stepper>
          </Box>

          <Button
            size="small"
            variant="outlined"
            onClick={syncEmailContacts}
            disabled={syncingEmailContacts}
            startIcon={syncingEmailContacts ? <CircularProgress size={14} /> : null}
            sx={{
              textTransform: "none",
              fontWeight: 800,
              borderColor: "#E5E7EB",
              color: "#111827",
              bgcolor: "white",
              flexShrink: 0,
              "&:hover": { borderColor: "#D1D5DB", bgcolor: "#F9FAFB" },
            }}
          >
            {syncingEmailContacts ? "Syncing…" : "Sync Contacts"}
          </Button>

          <Button
            size="small"
            onClick={handleCancel}
            disabled={loading || composerMeta.isSending}
            sx={{ textTransform: "none", fontWeight: 800, flexShrink: 0 }}
          >
            Cancel
          </Button>
        </Toolbar>
      </AppBar>

      <Box sx={{ bgcolor: "#F3F4F6", minHeight: "100vh", pb: activeStep === 1 ? 8 : 12 }}>
        <Box sx={{ px: { xs: 2, md: 3 }, py: activeStep === 1 ? 0 : { xs: 2, md: 3 }, width: "100%" }}>
          <Paper
            elevation={0}
            sx={{
              width: "100%",
              border: "1px solid #E5E7EB",
              borderRadius: activeStep === 1 ? 0 : 3,
              overflow: "hidden",
              bgcolor: "white",
            }}
          >
            <Box sx={{ p: activeStep === 1 ? 0 : { xs: 2, md: 3 }, width: "100%", display: "flex" }}>
              <Box sx={{ width: "100%", maxWidth: "100%" }}>
                {activeStep === 0 ? (
                  <AudienceBuilder
                    audienceChoice={audienceChoice}
                    onAudienceChoiceChange={setAudienceChoice}
                    source={source}
                    onSourceChange={setSource}
                    filters={filters}
                    onFiltersChange={setFilters}
                    loading={loading}
                    setLoading={setLoading}
                    excludeRecent={excludeRecent}
                    setExcludeRecent={setExcludeRecent}
                    recentDays={recentDays}
                    setRecentDays={setRecentDays}
                    pacingMode={pacingMode}
                    onPacingModeChange={setPacingMode}
                    pacingDays={pacingDays}
                    onPacingDaysChange={setPacingDays}
                    audienceSummary={audienceSummary}
                    previewRows={previewRows}
                    onAudiencePrepared={handleAudiencePrepared}
                    onImportRows={handleImportRows}
                    onUseAudience={handleUseAudience}
                    onFindSuccess={() => setActiveStep(1)}
                    emailBudget={emailBudget}
                    budgetLoading={budgetLoading}
                    canManageBudget={canManageEmailBudget}
                    onEmailBudgetUpdated={setEmailBudget}
                    intelligenceAudienceId={intelligenceAudienceId}
                  />
                ) : null}

                {activeStep === 1 ? (
                  <AudienceReview
                    audienceChoice={audienceChoice}
                    source={source}
                    filters={filters}
                    audiencePlan={audiencePlan}
                    audienceSummary={audienceSummary}
                    previewRows={previewRows}
                    audienceById={audienceById}
                    setAudienceById={setAudienceById}
                    selectedIds={selectedIds}
                    setSelectedIds={setSelectedIds}
                    suppressedEmails={audienceSummary?.suppressedEmails || []}
                    excludeRecent={excludeRecent}
                    recentDays={recentDays}
                    onGoBack={() => setActiveStep(0)}
                    onSampleExclude={handleSampleExclude}
                    onReviewBarChange={setReviewBarInfo}
                  />
                ) : null}

                {/* Composer stays mounted at step 3 (hidden) so launchRef.current is preserved */}
                {activeStep >= 2 ? (
                  <Box sx={{ display: activeStep === 2 ? undefined : "none" }}>
                    <Composer
                      audienceById={audienceById}
                      selectedIds={selectedIds}
                      audiencePlan={audiencePlan}
                      audienceSummary={audienceSummary}
                      previewRows={previewRows}
                      subject={subject}
                      setSubject={setSubject}
                      bodyHtml={bodyHtml}
                      setBodyHtml={setBodyHtml}
                      afterSendStatus={afterSendStatus}
                      setAfterSendStatus={setAfterSendStatus}
                      replyToMode={replyToMode}
                      setReplyToMode={setReplyToMode}
                      replyToCustomEmail={replyToCustomEmail}
                      setReplyToCustomEmail={setReplyToCustomEmail}
                      signature={signature}
                      excludeRecent={excludeRecent}
                      recentDays={recentDays}
                      pacingMode={pacingMode}
                      pacingDays={pacingDays}
                      onQueued={onQueued}
                      hidePrimaryAction
                      personalisationMode={personalisationMode}
                      setPersonalisationMode={setPersonalisationMode}
                      onMetaChange={setComposerMeta}
                      onLaunch={(getter) => {
                        try {
                          launchRef.current = getter?.() || null;
                        } catch {
                          launchRef.current = null;
                        }
                      }}
                    />
                  </Box>
                ) : null}

                {activeStep === 3 ? (
                  <>
                    {lastCampaignId ? (
                      <Alert severity="success" sx={{ mb: 2 }}>
                        <AlertTitle sx={{ fontWeight: 900 }}>Campaign queued successfully</AlertTitle>
                        Campaign #{lastCampaignId} · {Number(audienceSummary?.eligibleFound || 0).toLocaleString()} recipients ·{" "}
                        {scheduleLocal ? `Scheduled for ${fmtHarare(toHarareOffsetIso(scheduleLocal))}` : "Sending now"}
                        <Button size="small" onClick={() => setStatusDialogOpen(true)} sx={{ ml: 1 }}>
                          View status
                        </Button>
                      </Alert>
                    ) : null}
                    <CampaignConfirmation
                      audienceChoice={audienceChoice}
                      source={source}
                      filters={filters}
                      audienceSummary={audienceSummary}
                      subject={subject}
                      bodyHtml={bodyHtml}
                      selectedIds={selectedIds}
                      audiencePlan={audiencePlan}
                      signature={signature}
                      replyToMode={replyToMode}
                      replyToCustomEmail={replyToCustomEmail}
                    />
                  </>
                ) : null}
              </Box>
            </Box>

            {activeStep !== 1 ? (
              <>
                <Divider />
                <Box
                  sx={{
                    px: { xs: 2, md: 3 },
                    py: 1.5,
                    bgcolor: "#FAFAFA",
                    borderTop: "1px solid #E5E7EB",
                    display: "flex",
                    alignItems: "center",
                    gap: 1,
                    flexWrap: "wrap",
                  }}
                >
                  <Typography variant="caption" sx={{ color: "#6B7280" }}>
                    {activeStep === 0
                      ? "Tip: Select an audience — counts update automatically. Click Continue when ready."
                      : "Tip: Review your audience and message before scheduling. Once you launch, the campaign is queued immediately."}
                  </Typography>
                </Box>
              </>
            ) : null}
          </Paper>
        </Box>
      </Box>

      <Box
        sx={{
          position: "fixed",
          left: 0,
          right: 0,
          bottom: 0,
          bgcolor: "rgba(255,255,255,0.96)",
          backdropFilter: "blur(8px)",
          borderTop: "1px solid #E5E7EB",
          px: 2,
          py: 1,
          zIndex: 20,
        }}
      >
        <Box sx={{ px: { xs: 0, md: 1 }, display: "flex", alignItems: "center", gap: 1 }}>
          {activeStep > 0 ? (
            <Button
              onClick={() => setActiveStep((previous) => Math.max(0, previous - 1))}
              disabled={loading || composerMeta.isSending}
              sx={{ textTransform: "none", fontWeight: 900 }}
            >
              Back
            </Button>
          ) : null}

          {activeStep === 1 && reviewBarInfo ? (
            <Stack direction="row" spacing={1} alignItems="center" sx={{ flex: 1, overflow: "hidden", mx: 1 }}>
              <Typography variant="caption" color="text.secondary" noWrap sx={{ flexShrink: 0 }}>
                Untick to remove · cannot add
                {reviewBarInfo.removedCount > 0 ? ` · ${reviewBarInfo.removedCount.toLocaleString()} removed` : ""}
              </Typography>
              <Box sx={{ flex: 1 }} />
              {!reviewBarInfo.isImport ? (
                reviewBarInfo.totalPages > 1 ? (
                  <>
                    <Typography variant="caption" color="text.secondary" noWrap sx={{ flexShrink: 0 }}>
                      Page {reviewBarInfo.pageNum} of {reviewBarInfo.totalPages}
                    </Typography>
                    <Pagination
                      count={reviewBarInfo.totalPages}
                      page={reviewBarInfo.pageNum}
                      onChange={(_, v) => reviewBarInfo.onPageChange(v)}
                      size="small"
                      color="primary"
                    />
                  </>
                ) : (
                  <Typography variant="caption" color="text.secondary" noWrap sx={{ flexShrink: 0 }}>
                    {reviewBarInfo.currentPageCount.toLocaleString()} contacts
                    {reviewBarInfo.totalContacts > reviewBarInfo.currentPageCount ? ` of ${reviewBarInfo.totalContacts.toLocaleString()} matched` : ""}
                  </Typography>
                )
              ) : (
                <Typography variant="caption" color="text.secondary" noWrap sx={{ flexShrink: 0 }}>
                  {reviewBarInfo.currentPageCount.toLocaleString()} imported contacts
                </Typography>
              )}
            </Stack>
          ) : (
            <Box sx={{ flex: 1 }} />
          )}

          {/* step 0: Continue CTA is rendered inside AudienceBuilder */}

          {activeStep > 0 && activeStep < steps.length - 1 ? (
            <Button
              variant="contained"
              onClick={() => {
                if (!canMoveForward) return;
                if (activeStep === 1) {
                  handleNextFromReview();
                } else {
                  setActiveStep((previous) => previous + 1);
                }
              }}
              disabled={!canMoveForward || loading}
              sx={{ textTransform: "none", fontWeight: 900, px: 3 }}
            >
              Next
            </Button>
          ) : null}

          {activeStep === steps.length - 1 && !lastCampaignId ? (
            <>
              {composerMeta.isSending ? (
                <Box sx={{ display: "flex", alignItems: "center", gap: 1, px: 1 }}>
                  <CircularProgress size={18} />
                  <Typography variant="body2" color="text.secondary">Launching campaign…</Typography>
                </Box>
              ) : null}
              <Button
                variant="contained"
                color="success"
                onClick={() => handleConfirmSchedule(null)}
                disabled={!composerMeta.canSend || composerMeta.isSending}
                sx={{ textTransform: "none", fontWeight: 900, px: 3 }}
              >
                Send Now
              </Button>
              <Button
                variant="outlined"
                startIcon={<CalendarMonthIcon />}
                onClick={openScheduleDialog}
                disabled={!composerMeta.canSend || composerMeta.isSending}
                sx={{ textTransform: "none", fontWeight: 900, px: 3 }}
              >
                Schedule for Later
              </Button>
            </>
          ) : null}
        </Box>
      </Box>

      <Dialog
        open={scheduleOpen}
        onClose={() => setScheduleOpen(false)}
        maxWidth="sm"
        fullWidth
        TransitionProps={{
          onEnter: () => {
            if (!scheduleLocal || parseHarareDatetimeInput(scheduleLocal) <= new Date()) {
              setScheduleLocal(toHarareDatetimeValue(new Date(Date.now() + 60 * 60 * 1000)));
            }
          },
        }}
      >
        <DialogTitle sx={{ fontWeight: 900 }}>Schedule campaign</DialogTitle>
        <DialogContent>
          <Typography variant="body2" sx={{ color: "#6B7280", mb: 2 }}>
            Pick when the campaign should run. All times are in <strong>Harare time (UTC+2)</strong>.
          </Typography>

          <TextField
            label="Run date & time"
            type="datetime-local"
            fullWidth
            value={scheduleLocal}
            onChange={(event) => setScheduleLocal(event.target.value)}
            InputLabelProps={{ shrink: true }}
            inputProps={{ min: toHarareDatetimeValue(new Date(Date.now() + 2 * 60 * 1000)) }}
            helperText={
              scheduleLocal && parseHarareDatetimeInput(scheduleLocal)
                ? `Sends at ${fmtHarare(toHarareOffsetIso(scheduleLocal))} (Harare time)`
                : "Must be at least 2 minutes in the future."
            }
          />
        </DialogContent>
        <DialogActions sx={{ p: 2 }}>
          <Button onClick={() => setScheduleOpen(false)} sx={{ textTransform: "none", fontWeight: 800 }}>
            Cancel
          </Button>
          <Button
            variant="contained"
            color="success"
            onClick={() => handleConfirmSchedule()}
            sx={{ textTransform: "none", fontWeight: 900 }}
          >
            Confirm schedule
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog open={draftRestoreOpen} onClose={() => { setDraftRestoreOpen(false); setPendingDraft(null); }} maxWidth="xs" fullWidth>
        <DialogTitle sx={{ fontWeight: 900 }}>Unsaved draft found</DialogTitle>
        <DialogContent>
          <Typography variant="body2">
            You have an unsaved draft from{" "}
            {pendingDraft?.savedAt ? fmtHarare(pendingDraft.savedAt) : "earlier"}.
            Would you like to restore it?
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => { localStorage.removeItem("email_campaign_draft"); setDraftRestoreOpen(false); setPendingDraft(null); }}>
            Discard
          </Button>
          <Button variant="contained" onClick={handleRestoreDraft}>
            Restore draft
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog open={cancelWithDraftOpen} onClose={() => setCancelWithDraftOpen(false)} maxWidth="xs" fullWidth>
        <DialogTitle sx={{ fontWeight: 900 }}>Save draft before closing?</DialogTitle>
        <DialogContent>
          <Typography variant="body2">
            Your message and audience settings will be saved as a draft and restored the next time you open this form.
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => { localStorage.removeItem("email_campaign_draft"); setCancelWithDraftOpen(false); onClose?.(); }} color="error">
            Discard & Close
          </Button>
          <Button variant="contained" onClick={() => { setCancelWithDraftOpen(false); onClose?.(); }}>
            Save & Close
          </Button>
        </DialogActions>
      </Dialog>

      <Snackbar
        open={snack.open}
        autoHideDuration={3500}
        onClose={() => setSnack((current) => ({ ...current, open: false }))}
        anchorOrigin={{ vertical: "bottom", horizontal: "left" }}
      >
        <Alert severity={snack.type || "info"} onClose={() => setSnack((current) => ({ ...current, open: false }))}>
          {snack.msg}
        </Alert>
      </Snackbar>
    </Dialog>
  );
}

